import React, { useState } from "react";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { Zap, Heart, ShieldCheck, Leaf, Coffee, Gift, ChevronDown, ChevronUp } from "lucide-react";

const benefits = [
  {
    icon: Zap,
    title: "Rich in Protein",
    hindi: "प्रोटीन से भरपूर",
    desc: "9.7g protein per 100g — significantly higher than most snacks. Supports muscle building and sustained energy throughout the day.",
    stat: "9.7g/100g",
  },
  {
    icon: Heart,
    title: "Heart-Healthy",
    hindi: "हृदय के लिए लाभकारी",
    desc: "Extremely low in fat (0.1g/100g), zero cholesterol, and rich in Magnesium (67.2mg) for a healthy cardiovascular system.",
    stat: "0.1g Fat",
  },
  {
    icon: ShieldCheck,
    title: "100% Gluten-Free",
    hindi: "ग्लूटेन मुक्त",
    desc: "Naturally gluten-free, making it the perfect snack for celiac patients, gluten-intolerant individuals, and those on clean diets.",
    stat: "0% Gluten",
  },
  {
    icon: Leaf,
    title: "Fasting Superfood",
    hindi: "व्रत का पवित्र आहार",
    desc: "The traditional choice for Navratri, Ekadashi, and all Indian fasting days. Pure, sattvic, and energy-rich — perfect for vratas.",
    stat: "Vrat Safe",
  },
  {
    icon: Coffee,
    title: "Low Calorie Snack",
    hindi: "कम कैलोरी, ज़्यादा पोषण",
    desc: "347 kcal per 100g but highly filling due to rich fiber content. The ideal guilt-free snack for weight-conscious modern consumers.",
    stat: "347 kcal",
  },
  {
    icon: Gift,
    title: "Premium Gifting",
    hindi: "उपहार के लिए उत्तम",
    desc: "Our 6 Suta luxury grade makes for exceptional corporate and festive gifting — elegant, health-conscious, and deeply traditional.",
    stat: "6 Suta Luxury",
  },
];

const nutrition = [
  { name: "Protein", value: "9.7g" },
  { name: "Carbohydrates", value: "76.9g" },
  { name: "Fat", value: "0.1g" },
  { name: "Fiber", value: "0.2g" },
  { name: "Calcium", value: "60mg" },
  { name: "Magnesium", value: "67.2mg" },
  { name: "Iron", value: "1.4mg" },
  { name: "Energy", value: "347 kcal" },
];

const ValueAddSection = () => {
  const { ref, isVisible } = useScrollReveal();
  const [showNutrition, setShowNutrition] = useState(false);

  return (
    <section id="value-add" ref={ref} className="py-24 bg-[#050505]" data-testid="value-add-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div
          className="text-center mb-16"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(30px)",
            transition: "opacity 0.6s ease, transform 0.6s ease",
          }}
        >
          <span className="text-[#D4AF37] text-xs uppercase tracking-[5px] mb-4 block font-medium">
            Health & Nutrition
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold text-[#FAFAFA] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="value-add-heading"
          >
            Why Choose MakhanaG?
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
            Bihar's ancient superfood meets modern nutrition science. Discover
            why health-conscious India is switching to premium makhana.
          </p>
          <p
            className="text-[#D4AF37]/80 mt-2 text-base"
            style={{ fontFamily: "'Noto Sans Devanagari', sans-serif" }}
          >
            बिहार का सुपरफूड — आधुनिक जीवनशैली के लिए
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {benefits.map((benefit, i) => {
            const Icon = benefit.icon;
            return (
              <div
                key={benefit.title}
                className="bg-[#121212] border border-white/5 hover:border-[#D4AF37]/30 rounded-2xl p-6 transition-all duration-400 hover:shadow-[0_10px_40px_rgba(212,175,55,0.1)] group"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? "translateY(0)" : "translateY(30px)",
                  transition: `opacity 0.6s ease ${0.1 + i * 0.1}s, transform 0.6s ease ${0.1 + i * 0.1}s`,
                }}
                data-testid={`benefit-card-${i}`}
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-[#D4AF37]/10 group-hover:bg-[#D4AF37]/20 transition-colors shrink-0">
                    <Icon className="text-[#D4AF37]" size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-[#FAFAFA] font-semibold text-base">{benefit.title}</h3>
                      <span className="text-[10px] text-[#D4AF37] border border-[#D4AF37]/30 px-2 py-0.5 rounded-full font-medium shrink-0 ml-2">
                        {benefit.stat}
                      </span>
                    </div>
                    <p
                      className="text-[#D4AF37]/70 text-xs mb-2"
                      style={{ fontFamily: "'Noto Sans Devanagari', sans-serif" }}
                    >
                      {benefit.hindi}
                    </p>
                    <p className="text-[#A1A1AA] text-sm leading-relaxed">{benefit.desc}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Nutrition Accordion */}
        <div
          className="bg-[#121212] border border-white/5 rounded-2xl overflow-hidden"
          style={{
            opacity: isVisible ? 1 : 0,
            transition: "opacity 0.6s ease 0.7s",
          }}
          data-testid="nutrition-accordion"
        >
          <button
            onClick={() => setShowNutrition(!showNutrition)}
            className="w-full flex items-center justify-between p-6 md:p-8 text-left hover:bg-[#1E1E1E] transition-colors"
            data-testid="nutrition-toggle"
          >
            <div>
              <h3
                className="text-[#FAFAFA] text-xl font-bold"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Nutritional Information
              </h3>
              <p className="text-[#A1A1AA] text-sm mt-1">
                Per 100g serving of Premium Raw Makhana
              </p>
            </div>
            {showNutrition ? (
              <ChevronUp className="text-[#D4AF37] shrink-0" size={22} />
            ) : (
              <ChevronDown className="text-[#D4AF37] shrink-0" size={22} />
            )}
          </button>

          <div
            className={`overflow-hidden transition-all duration-500 ${
              showNutrition ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
            }`}
          >
            <div className="px-6 md:px-8 pb-8">
              <div className="h-px bg-[#D4AF37]/10 mb-6" />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {nutrition.map((item, i) => (
                  <div
                    key={item.name}
                    className="text-center p-4 rounded-xl bg-[#0A0A0A] border border-white/5 hover:border-[#D4AF37]/20 transition-colors"
                    data-testid={`nutrition-item-${i}`}
                  >
                    <div
                      className="text-2xl font-bold text-[#D4AF37] mb-1"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {item.value}
                    </div>
                    <div className="text-[#A1A1AA] text-xs">{item.name}</div>
                  </div>
                ))}
              </div>
              <p className="text-[#525252] text-xs text-center mt-4">
                * Approximate values. Actual nutritional content may vary by grade and batch.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValueAddSection;
